import React, { useState, useEffect, useId } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { faQuestionCircle, faRightToBracket } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { selectUser } from '../../../redux/userSlice'
import { selectAuth } from '../../../redux/authSlice'
import { selectModules } from '../../../redux/moduleSlice'
import { setNotification } from '../../../redux/notificationSlice'
import { hasUserPermission } from '../../../utils/permissionHelper'
import LayoutSidebar from '../../../components/LayoutSidebar'
import Loading from '../../../components/Loading'
import TableHeader from '../../../components/TableHeader'
import TableRow from '../../../components/TableRow'
import Pagination from '../../../components/Pagination'
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import CutoffTimeDashlet from '../../dashlets/CutoffTimeDashlet'
import { selectPortfolios } from "../../../redux/portfolioSlice"
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { buildQueryString } from '../../../helpers/formatting'
/**
 * Main list page for all of LOX Admin. This page is controlled by the BE module system and renders
 *  the table and the data for the table dynamically based on that system.
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export default function WireApprovalQueue(props) {
  const params = useParams()
  const navi = useNavigate()
  const startId = useId()
  const dispatch = useDispatch()

  // Redux Selectors
  const modules = useSelector(selectModules)
  const active = useSelector(selectUser)
  const auth = useSelector(selectAuth)
  const requests = useRequest()

  // Local State
  const [module, setModule] = useState()
  const [moduleCount, setModuleCount] = useState(-1)
  const [loaded, setLoaded] = useState(true)
  const [partialLoad, setPartialLoad] = useState(false)
  const [searchFilter, setSearchFilter] = useState('')
  const [urlFilter, setUrlFilter] = useState('')
  const [validPortfolios, setValidPortfolios] = useState([]);
  const [userPermissions, setUserPermissions] = useState()
  const portfolios = useSelector(selectPortfolios)
  const [portfolioId, setPortfolioId] = useState(null);
  const [loanId, setLoanId] = useState(null);
  const { control, setValue } = useForm();
  const [moduleData, setModuleData] = useState([])
  const [moduleFields, setModuleFields] = useState()

  const displayFields = ['id', 'guid', 'customer_id', 'lead_status_dropdown_option_id', 'payment_status_dropdown_option_id', 'principal_amount', 'effective_date']

  useEffect(() => {
    if (active.status === 'succeeded' && module) {
      const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
      setUserPermissions(activePermissions)
      if (!hasUserPermission(activePermissions, `${module.code_key}_read`)) {
        navi('/403')
      }
    }
  }, [active, module])

  useEffect(() => {
    if (active.data.limit_to_portfolio_id || active?.spoofed_user?.limit_to_portfolio_id) {
      let limitedPortfolioIds = active?.spoofed_user?.limit_to_portfolio_id ?? (active.data.limit_to_portfolio_id);
      let limitedPortfolio = []
      limitedPortfolioIds.forEach((portfolioId) => {
        let portfolioData = portfolios.data.filter((portfolio) => portfolio.id === portfolioId);
        portfolioData = portfolioData[0] ?? null
        if (portfolioData) {
          limitedPortfolio.push(portfolioData)
        }
      })
      setValidPortfolios(limitedPortfolio)
    } else {
      setValidPortfolios(portfolios.data);
    }
  }, [active, portfolios])

  /**
   * Ensure that the module being accessed actually exists
   */
  const calculateDisplayRank = (item, fields) => {
    return fields.indexOf(item)
  }

  useEffect(() => {
    const m = modules.data.find((e) => e.code_key === 'payment');
    const ld = modules.data.find((e) => e.code_key === 'lead');
    const ln = modules.data.find((e) => e.code_key === 'loan');
    if (m) {
      setModule(m);

      const mf = [...m.module_fields, ...ld.module_fields, ...ln.module_fields];

      // Filter and sort while keeping column_name unique
      const uniqueFilteredAndSorted = mf
        .filter((m) => {
          if (m.column_name !== 'id' && !(m.column_name == "guid" && m.module_code_key == 'payment')) {
            const rank = calculateDisplayRank(m.column_name, displayFields);
            return rank >= 0;
          }
        })
        .reduce((acc, current) => {
          const x = acc.find((item) => item.column_name === current.column_name);
          if (!x) {
            return acc.concat([current]);
          } else {
            return acc;
          }
        }, [])
        .sort((a, b) => calculateDisplayRank(a.column_name, displayFields) - calculateDisplayRank(b.column_name, displayFields));

      setModuleFields(uniqueFilteredAndSorted);
    } else {
      navi('/404');
    }
  }, [params.module_name]);

  const getWireApprovalQueuePayments = async (url) => {
    try {
      setPartialLoad(false)
      requests.setEndpoint(url)
      const response = await requests.get()
      response.data.data = response.data.data.map((item) => {
        return {
          ...item,
          module_code_key: 'wire_approval_queue'
        }
      });

      setModuleData(response.data)
      setPartialLoad(true)
    } catch (error) {
      setPartialLoad(true)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const handleSearch = async () => {
    let url = `${process.env.REACT_APP_API_DOMAIN}/portfolio/${portfolioId}/payment/wire-approval-queue`
    let params = {}
    if (loanId) {
      params["loan_id"] = loanId;
    }
    url += buildQueryString(params)
    getWireApprovalQueuePayments(url)
  }

  useEffect(() => {
    if (portfolioId) {
      setValue('portfolio_id', { label: validPortfolios?.find((o) => o.id === portfolioId)?.name, value: portfolioId })
        handleSearch();
    }
  }, [portfolioId])

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken) {
      setPortfolioId(validPortfolios?.find((o) => o.id != null)?.id)
    }
  }, [validPortfolios, auth.accessToken])

  /**
   * After the API call for module data succeeds stop loading and set the record count
   */
  useEffect(() => {
    if (moduleData && Object.keys(moduleData).length) {
      setModuleCount(moduleData.total)
    } else {
      setModuleCount(moduleData.data && moduleData.data.length)
    }
  }, [moduleData])

  if (module) {
    const count = 0

    const contentTitle = (moduleCount !== -1) ? (
      <>
        <span>
          Payments: Wire Approval Queue&nbsp;
        </span>
        <span>
          ({moduleCount})
        </span>
        {module.helper_text && (
          <span className="lox-no-border ml-1 lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary" data-tooltip={module.helper_text}>
            <FontAwesomeIcon icon={faQuestionCircle} className="is-size-4 has-text-link" />
          </span>
        )}
      </>
    ) : (
      <>
        <span>
          {module.name} List&nbsp;
        </span>
        <Loading size="is-size-4" />
      </>
    )

    return (
      <LayoutSidebar
        contentTitle={contentTitle}
        content={loaded || partialLoad ? (
          <div className="has-pagination pt-3 mb-5">
            <div className="columns m-2">
              <div className="field is-horizontal">
                <label htmlFor="portfolio_id" style={{ width: '80px' }}>Portfolio:</label>
                <div className="control" style={{ width: '300px' }}>
                  <Controller
                    name="portfolio_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isSearchable={false}
                        classNames={reactSelectClassNames()}
                        options={validPortfolios?.filter((o) => o.id != null).map((o) => ({
                          label: o.name,
                          value: o.id,
                        }))}
                        onChange={(val) => {
                          setPortfolioId(val?.value)
                          field.onChange({ label: val?.label, value: val?.value })
                        }}
                        menuPosition="fixed"
                        styles={{ menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="field is-horizontal ml-6">
                <label htmlFor="portfolio_id" style={{ width: '50px' }}>Loan:</label>
                <div className="control" style={{ width: '100px' }}>
                  <Controller
                    name="loan_id"
                    control={control}
                    defaultValue={null}
                    render={({ field }) => (
                      <input
                        {...field}
                        className="input is-small"
                        type="text"
                        onChange={(evt) => {
                          setLoanId(evt.target.value)
                        }}
                      />
                    )}
                  />

                </div>
              </div>
              <div className="field is-horizontal ml-2">
                <button
                  type="button"
                  className="button is-link has-text-white is-small"
                  onClick={handleSearch}
                  disabled={!partialLoad}
                >
                  <span className="mx-3">Search</span>
                </button>
              </div>
            </div>

            <div
              className={`${moduleData.data?.length <= 3 && 'lox-hooktable-dataless'} lox-hooktable table-container mb-0 position-relative`}
            >
              {!partialLoad && loaded && (
                <div className="loader-wrapper">
                  <Loading />
                </div>
              )}
              <div className="list-table-container">
                <table
                  className="lox-hooktable-table table is-striped is-narrow is-hoverable is-fullwidth is-info has-sticky-column has-view-button"
                >
                  <TableHeader
                    columns={moduleFields}
                    startId={startId}
                    count={count}
                    hasActions={false}
                    hasView
                    setPartialLoad={setPartialLoad}
                    url={module.code_key + urlFilter}
                    searchFilter={searchFilter}
                    module={module}
                    extraAction={[faRightToBracket]}
                  />
                  {moduleData?.data?.length === 0 && (
                    <tbody>
                    <tr>
                      <td
                        className="is-size-6 is-italic"
                        style={{ textAlign: 'center', padding: '20px', color: 'grey' }}
                        colSpan={displayFields.length + 1}
                      >No Record(s) Found
                      </td>
                    </tr>
                    </tbody>
                  )}
                  <tbody className={partialLoad && !loaded ? 'loading' : ''}>
                  <TableRow
                    rows={moduleData.data}
                    columns={moduleFields}
                    hasActions={false}
                    hasView
                    baseName={module.code_key}
                    setPartialLoad={setPartialLoad}
                  />
                  </tbody>
                </table>
              </div>
            </div>
            {/* If there is more than one page, show pagination */}
            {moduleData && Object.keys(moduleData).length > 0 && moduleData.last_page !== 1 && (
              <Pagination
                meta={moduleData}
                setPartialLoad={() => {
              }}
                isCustomCall
                setDataFunction={getWireApprovalQueuePayments}
              />
            )}
          </div>
        ) : <Loading />}
        dashlets={<CutoffTimeDashlet />}
      />
    )
  }
}
